import { flash, SubmitButton, Switch, Textarea } from "@xola/ui-kit";
import { Field, FieldProps, FormikProps } from "formik";
import { uniqueId } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { ConversionTracking } from "types";
import { api } from "../../api/api";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { useInvalidate } from "../../hooks/useInvalidation";
import { SecondaryButton } from "../Common/SecondaryButton";
import { VariableItem, VariablesButton } from "../Common/VariablesButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";
import { ConversionTrackingTermsModal } from "./ConversionTrackingTermsModal";

const initialValue: ConversionTracking = {
    enabled: false,
    code: "",
    label: "Custom",
};

const variables: VariableItem[] = [
    { name: "{{order.amount}}", title: "Amount", type: "Number" },
    { name: "{{order.purchaseAmount}}", title: "Purchase Amount PreTax/Fee", type: "Number" },
    { name: "{{order.currency}}", title: "Currency code", type: "String" },
    { name: "{{order.id}}", title: "Purchase ID", type: "String" },
    { name: "{{order.productId}}", title: "Product ID", type: "String" },
    { name: "{{order.productName}}", title: "Product Name", type: "String" },
    { name: "{{order.productType}}", title: "Product Type", type: "String" },
    { name: "{{order.quantity}}", title: "Total Demographics", type: "Number" },
    { name: "{{order.arrival}}", title: "Arrival Date", type: "String" },
    { name: "{{order.couponCode}}", title: "Coupon Code", type: "String" },
    { name: "{{session}}", title: "Session ID", type: "String" },
    { name: "{{order.seller.id}}", title: "Seller ID", type: "String" },
    { name: "{{order.seller.name}}", title: "Company Name", type: "String" },
    { name: "{{order.seller.city}}", title: "Company City", type: "String" },
    { name: "{{order.seller.state}}", title: "Company State", type: "String" },
    { name: "{{order.seller.country}}", title: "Company Country", type: "String" },
    { name: "{{order.seller.countryCode}}", title: "Company Country Code", type: "String" },
];

export const CustomConversionTracking = () => {
    const [codeInputId] = useState(uniqueId("CustomConversionTrackingCodeInput"));
    const [showTermsModal, setShowTermsModal] = useState(false);
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [preference = initialValue] = seller.preferences.conversionTrackings;

    const { mutateAsync: upsertTracking, isLoading } = useMutation(async (payload: ConversionTracking) => {
        const hasAtLeastOnePersisted = seller.preferences.conversionTrackings.length > 0;
        const url = `/users/${seller.id}/preferences/conversionTrackings`;
        await (hasAtLeastOnePersisted ? api.put(`${url}/${preference.id}`, payload) : api.post(url, payload));
        invalidate("sellers", seller.id);
        flash.success("Successfully updated");
    });

    const handleInsertVariable = (variable: string, form: FormikProps<ConversionTracking>) => {
        // TODO: We should use refs, but ui-kit doesn't support refs for all elements at the moment
        const codeInputElement: HTMLTextAreaElement = document.querySelector(`#${codeInputId}`)!;

        // Get the current selection
        const value = form.values.code;
        const start = codeInputElement.selectionStart;
        const end = codeInputElement.selectionEnd;

        // Insert the variable at the current position
        form.setFieldValue("code", [value.slice(0, start), variable, value.slice(end)].join(""));

        // Move the selection back to the right position
        // Timeout is required since we want to focus back to the textarea when all other updates are done
        setTimeout(() => {
            codeInputElement.focus();
            codeInputElement.setSelectionRange(start + variable.length, start + variable.length);
        }, 10);
    };

    const handleCodeKeyDown = (e: any, form: FormikProps<ConversionTracking>) => {
        if (e.key === "Tab") {
            e.preventDefault();
            handleInsertVariable("    ", form);
        }
    };

    const handleSave = (values: ConversionTracking) => {
        upsertTracking(values);
    };

    return (
        <XolaFormik enableReinitialize initialValues={preference} onSubmit={handleSave}>
            {({ values, handleSubmit, handleReset, setFieldValue }) => {
                return (
                    <ContentLayout isWide isDisabled={isLoading} className="space-y-6">
                        <ContentLayout.Section
                            title="Custom Conversion Tracking"
                            description="Custom conversion tracking  allows you to input and save a custom script that will run inside of checkout when a new purchase is made"
                        >
                            <div className="space-y-2 rounded bg-green-lighter p-3">
                                <Switch.Group>
                                    <Switch
                                        isChecked={values.enabled}
                                        size="large"
                                        onChange={(enabled: boolean) => {
                                            if (enabled) {
                                                setShowTermsModal(true);
                                            } else {
                                                setFieldValue("enabled", false);
                                            }
                                        }}
                                    />

                                    <Switch.Label direction="left" className="ml-3">
                                        {values.enabled ? "Enabled" : "Disabled"}
                                    </Switch.Label>
                                </Switch.Group>

                                <div data-testid="custom-conversion-tracking-status-text">
                                    Conversion tracking code is currently {values.enabled ? "active" : "inactive"}
                                </div>
                            </div>
                        </ContentLayout.Section>

                        <hr className="my-2 text-gray-light" />

                        {values.enabled && (
                            <ContentLayout.Section
                                title="Code"
                                description="Enter in HTML code that you would like to be executed within checkout after a completed purchase."
                            >
                                <div className="flex items-center justify-between">
                                    <div className="font-bold">
                                        HTML Code Executed in Checkout Upon Purchase Conversion
                                    </div>

                                    <Field>
                                        {({ form }: FieldProps) => (
                                            <VariablesButton
                                                items={variables}
                                                onInsertVariable={(e, variable) => {
                                                    handleInsertVariable(variable, form);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </div>

                                <Field>
                                    {({ form }: FieldProps) => (
                                        <Textarea
                                            id={codeInputId}
                                            className="mt-2 font-mono text-sm leading-6"
                                            rows={8}
                                            value={values.code}
                                            onChange={(e: any) => {
                                                setFieldValue("code", e.target.value);
                                            }}
                                            onKeyDown={(e: any) => {
                                                handleCodeKeyDown(e, form);
                                            }}
                                        />
                                    )}
                                </Field>
                            </ContentLayout.Section>
                        )}

                        <ContentLayout.Footer>
                            <SubmitButton isLoading={isLoading} onClick={handleSubmit}>
                                Save
                            </SubmitButton>

                            <SecondaryButton type="reset" onClick={handleReset}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ConversionTrackingTermsModal
                            isOpen={showTermsModal}
                            onClose={(isAccepted) => {
                                setFieldValue("enabled", isAccepted);
                                setShowTermsModal(false);
                            }}
                        />

                        <ScrollToFieldError />
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};
